import { useState, useEffect, useCallback } from 'react';
import {
	ICustomColumnsConfig,
	ICustomColumn,
} from '../modules/admin-panel/components/comments-block/components/ai-insights/actions/types/ActionPlanInterfaces';

const STORAGE_KEY = 'actionReportCustomColumns';

interface IUseCustomColumnsReturn {
	customColumnsConfig: ICustomColumnsConfig;
	saveCustomColumnsConfig: (config: ICustomColumnsConfig) => void;
	updateCustomColumnData: (
		actionId: string,
		columnId: string,
		value: string,
	) => void;
	saveColumnWidths: (widths: { [key: string]: number }) => void;
	saveColumnOrder: (order: string[]) => void;
	saveColumnNames: (names: { [key: string]: string }) => void;
	saveHiddenColumns: (hidden: string[]) => void;
	clearCustomColumnsConfig: () => void;
}

export const useCustomColumns = (
	surveyId?: string,
): IUseCustomColumnsReturn => {
	const [customColumnsConfig, setCustomColumnsConfig] = useState<
		ICustomColumnsConfig
	>({
		columns: [],
		data: {},
		columnWidths: {},
		columnOrder: [],
		columnNames: {},
	});

	const getStorageKey = useCallback((id?: string) => {
		return id ? `${STORAGE_KEY}_${id}` : STORAGE_KEY;
	}, []);

	useEffect(() => {
		const loadConfig = () => {
			try {
				const stored = localStorage.getItem(getStorageKey(surveyId));
				if (stored) {
					const parsedConfig = JSON.parse(stored) as ICustomColumnsConfig;
					setCustomColumnsConfig(parsedConfig);
				}
			} catch (error) {
				console.warn(
					'Failed to load custom columns config from localStorage:',
					error,
				);
			}
		};

		loadConfig();
	}, [surveyId, getStorageKey]);

	const saveCustomColumnsConfig = useCallback(
		(config: ICustomColumnsConfig) => {
			try {
				localStorage.setItem(getStorageKey(surveyId), JSON.stringify(config));
				setCustomColumnsConfig(config);
			} catch (error) {
				console.error(
					'Failed to save custom columns config to localStorage:',
					error,
				);
			}
		},
		[surveyId, getStorageKey],
	);

	const updateCustomColumnData = useCallback(
		(actionId: string, columnId: string, value: string) => {
			setCustomColumnsConfig((prevConfig) => {
				const newData = {
					...prevConfig.data,
					[actionId]: {
						...prevConfig.data[actionId],
						[columnId]: value,
					},
				};

				const newConfig = {
					...prevConfig,
					data: newData,
				};

				try {
					localStorage.setItem(
						getStorageKey(surveyId),
						JSON.stringify(newConfig),
					);
				} catch (error) {
					console.error(
						'Failed to update custom column data in localStorage:',
						error,
					);
				}

				return newConfig;
			});
		},
		[surveyId, getStorageKey],
	);

	const saveColumnWidths = useCallback(
		(widths: { [key: string]: number }) => {
			setCustomColumnsConfig((prevConfig) => {
				const newConfig = {
					...prevConfig,
					columnWidths: widths,
				};

				try {
					localStorage.setItem(
						getStorageKey(surveyId),
						JSON.stringify(newConfig),
					);
				} catch (error) {
					console.error('Failed to save column widths to localStorage:', error);
				}

				return newConfig;
			});
		},
		[surveyId, getStorageKey],
	);

	const saveColumnOrder = useCallback(
		(order: string[]) => {
			setCustomColumnsConfig((prevConfig) => {
				const newConfig = {
					...prevConfig,
					columnOrder: order,
				};

				try {
					localStorage.setItem(
						getStorageKey(surveyId),
						JSON.stringify(newConfig),
					);
				} catch (error) {
					console.error('Failed to save column order to localStorage:', error);
				}

				return newConfig;
			});
		},
		[surveyId, getStorageKey],
	);

	const saveColumnNames = useCallback(
		(names: { [key: string]: string }) => {
			setCustomColumnsConfig((prevConfig) => {
				const newConfig = {
					...prevConfig,
					columnNames: names,
				};

				try {
					localStorage.setItem(
						getStorageKey(surveyId),
						JSON.stringify(newConfig),
					);
				} catch (error) {
					console.error('Failed to save column names to localStorage:', error);
				}

				return newConfig;
			});
		},
		[surveyId, getStorageKey],
	);

	const saveHiddenColumns = useCallback(
		(hidden: string[]) => {
			setCustomColumnsConfig((prevConfig) => {
				const newConfig = {
					...prevConfig,
					hiddenColumns: hidden,
				};

				try {
					localStorage.setItem(
						getStorageKey(surveyId),
						JSON.stringify(newConfig),
					);
				} catch (error) {
					console.error(
						'Failed to save hidden columns to localStorage:',
						error,
					);
				}

				return newConfig;
			});
		},
		[surveyId, getStorageKey],
	);

	const clearCustomColumnsConfig = useCallback(() => {
		try {
			localStorage.removeItem(getStorageKey(surveyId));
			setCustomColumnsConfig({
				columns: [],
				data: {},
				columnWidths: {},
				columnOrder: [],
				columnNames: {},
			});
		} catch (error) {
			console.error(
				'Failed to clear custom columns config from localStorage:',
				error,
			);
		}
	}, [surveyId, getStorageKey]);

	return {
		customColumnsConfig,
		saveCustomColumnsConfig,
		updateCustomColumnData,
		saveColumnWidths,
		saveColumnOrder,
		saveColumnNames,
		saveHiddenColumns,
		clearCustomColumnsConfig,
	};
};
